import { Box } from "@mui/material";
import { CellClickedEvent, ColDef, FilterChangedEvent, FirstDataRenderedEvent, GetRowIdFunc, GridApi, GridReadyEvent, IRowNode, InitialGroupOrderComparatorParams, PostSortRowsParams } from "ag-grid-community";
import { Ref, useEffect, useRef } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
//import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { GridData } from "../ag-grid-extensions/models/GridData";
import { MemoCellRenderer } from "../ag-grid-extensions/renderers/MemoCellRenderer";
import ShortcutsCellRenderer from "../ag-grid-extensions/renderers/ShortcutsCellRenderer";
import { SparklineCellRenderer } from "../ag-grid-extensions/renderers/SparklineCellRenderer";
import StandardCellRenderer from "../ag-grid-extensions/renderers/StandardCellRenderer";
import { SystemBalanceCellRenderer } from "../ag-grid-extensions/renderers/SystemBalanceCellRenderer";
import DefaultGrid, { DefaultGridRef } from "../default-grid/DefaultGrid";
import { RowDef } from "../ag-grid-extensions/models/RowDef";
import { MemoCellRendererV2 } from "../ag-grid-extensions/renderers/MemoCellRendererV2";
import { OptionalColumnEnum } from "../../common/enums/DRCXEnums";

const defaultColDef: ColDef = {
    sortable: false,
    filter: "agMultiColumnFilter",
    resizable: true

    // clear column state
    // aggFunc: null,
    // pinned: null,
    // sort: null,
    // sortIndex: null
};

const autoGroupColumnDef = {
    headerName: "Type",
    sortable: true
};

interface SummaryListingGridProps {
    show?: boolean;
    gridData: GridData;
    showFooter?: boolean;
    showGroupingFooter?: boolean;
    filterText?: string;

    configurationID?: number;
    hideContextMenu?: boolean;

    domLayout?: 'normal' | 'autoHeight' | 'print';
    ref?: Ref<DefaultGridRef>;

    isMultiSection?: boolean;
    pagination?: boolean;
    pageSize?: number;

    // Callbacks
    postSort?: (rowNodes: Array<IRowNode>) => void;
    groupComparator?: (nodeA: IRowNode, nodeB: IRowNode) => -1 | 0 | 1;
    onFilterChanged?: (event: FilterChangedEvent) => void;
    onGridReady?: (event: GridReadyEvent) => void;
    onFirstDataRendered?: (event: FirstDataRenderedEvent) => void;
    getRowId?: GetRowIdFunc<RowDef>;

    disableNavigation?: boolean;


}

export default function SummaryListingGrid({
    show,
    gridData,
    showFooter,
    showGroupingFooter,
    filterText,
    configurationID,
    hideContextMenu = false,
    postSort,
    groupComparator,
    domLayout,
    onFilterChanged,
    onGridReady,
    ref,
    isMultiSection,
    pageSize,
    pagination,
    getRowId,
    disableNavigation
}: SummaryListingGridProps) {
    //const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
    //const [columnApi, setColumnApi] = useState<ColumnApi>();

    let gridApi: GridApi | null = null;

    const navigateTo = useNavigateTo();
    // const routeContext = useRouteContext();

    // cannot use state for storing the column(s) pin state because it has to be applied
    // to the grid after it renders and can cause issues with rerendering and logic to
    // clear or reset the pinned state.
    // const columnStateRef = useRef<ColumnState[]>();

    const isPhone = isMobile && !isTablet;

    //console.log(`DataGrid render showFooter: ${showFooter}, showGroupingFooter: ${showGroupingFooter}`);

    // This probably shouldn't use the layout context directly
    //  const [, contentLayoutContextDispatch] = useContentLayoutContext();

    function _onGridReady(event: GridReadyEvent) {
        gridApi = event.api;
        //setGridApi(event.api);
        //setColumnApi(event.columnApi);
        onGridReady && onGridReady(event);

        // event.api.sizeColumnsToFit() 

        // const scrollTarget = (event.api as any).gridPanel.eBodyViewport;
        // contentLayoutContextDispatch({ type: "scrollTargetSet", scrollTarget: scrollTarget ?? undefined });
    }

    // Had to use these references, otherwise old values were
    // being captured via closure.
    const postSortFunc = useRef<((rowNodes: Array<IRowNode>) => void) | undefined>(undefined);
    const groupComparatorFunc = useRef<((nodeA: IRowNode, nodeB: IRowNode) => -1 | 0 | 1) | undefined>(undefined);

    useEffect(() => {
        postSortFunc.current = postSort;
        gridApi?.refreshClientSideRowModel("sort");
    }, [postSort, gridApi]);

    useEffect(() => {
        groupComparatorFunc.current = groupComparator;
        gridApi?.refreshClientSideRowModel("group");
    }, [gridApi, groupComparator]);

    // SPR - These two effects were causing problems with the grid auto sizing.
    // They were put in place for user story #48286, but they are not needed
    // because the latest grid maintains its column settings without the extra logic.
    // useEffect(() => {
    //     columnStateRef.current = undefined;
    // }, [
    //     routeContext.groupID,
    //     configurationID
    // ]);

    // useEffect(() => {
    //     if (columnStateRef.current) {
    //         //console.log('applycolumnstate')
    //         gridApi?.applyColumnState({
    //             state: columnStateRef.current
    //         });
    //     }
    // });

    //     useEffect(() => {
    //         // gridApi?.setColumnDefs(gridData.cols);
    //         // gridApi?.setRowData(gridData.rows);

    //         if(gridApi?.getPinnedBottomRowCount()){
    //             // const footerRow = gridApi?.getPinnedBottomRow(0);
    //             // gridApi?.redrawRows({ rowNodes: [footerRow] });
    //             // gridApi.refreshClientSideRowModel("aggregate");
    //             gridApi?.setPinnedBottomRowData([{}]);
    // // crap, this works but it's pokey slow
    //             window.setTimeout(() => {
    //                 const footerRow = gridApi?.getPinnedBottomRow(0);
    //                 //gridApi?.redrawRows({ rowNodes: [footerRow] });
    //                 gridApi.refreshClientSideRowModel("map");
    //             }, 0);

    //         } else {
    //            gridApi?.setPinnedBottomRowData([]);
    //         }
    //     }, [gridData, gridApi])

    // console.log(`${Date.now()} - Rendering grid - pinFooter: ${pinFooter}, pinnedBottomRowData: ${showFooter && pinFooter}, groupIncludeFooter: ${showGroupingFooter}, groupIncludeTotalFooter: ${showFooter && !pinFooter}`);

    // This is probably overkill. Device and well details have requirements that the other tabs don't.
    // Device is the only one that cares about system balance group, so why should the other tabs carry the
    // baggage of support.
    // Same with the sort on comms alarm first stuff. Only devices and wells cares about that.

    // It appears the difficulty with the pinned header can be solved with the show flag below.
    // Maybe I can use a flag to tell when a different configuration is being loaded.

    const onCellClicked = (event: CellClickedEvent): void => {
        
        // DO NOT use <a> with href it will cause full page reload
        // only use the router to navigate 
        if (!disableNavigation && event.colDef.field && event.value.optionalColumn !== OptionalColumnEnum.Memos) {

            let options: NavigateToOptions | undefined;

            const mouseEvent = event.event as MouseEvent;
            if (mouseEvent && (mouseEvent.shiftKey || mouseEvent.ctrlKey)) {
                options = {
                    newViewType: mouseEvent.shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
                };
            }

            let href: string | undefined = event.data[event.colDef.field]?.href;

            if (href) {
                navigateTo(href, options);
            }
        }
    };

    // SPR - See comment above about the column state.
    // const onColumnPinned = (event: ColumnPinnedEvent) => {
    //     //console.log(`pinned source ${event.source}`);
    //     // if (event.source === 'contextMenu') {
    //     //     const stateInfo = gridApi?.getColumnState();

    //     //     const pinnedState = stateInfo?.map((o) => ({
    //     //         colId: o.colId,
    //     //         pinned: o.pinned
    //     //     }));

    //     //     columnStateRef.current = pinnedState;
    //     // }
    // };

    return (
        <Box
            sx={{
                //height: 1, 
                flex: '1 1 auto',
                display: show ? 'flex' : 'none',
                flexDirection: 'column',
                flexShrink: 1,
                flexGrow: 1
            }}>
            {/* sx={{ 
                height: 1, 
                //flex: '1 1 auto',
                display: show ? 'undefined' : 'none',
                //flexDirection: 'column'
                 }}> */}


            <DefaultGrid
                ref={ref}
                showFooter={showFooter}
                onGridReady={
                    _onGridReady
                }
                onCellClicked={onCellClicked}
                rowData={gridData.rows}
                defaultColDef={
                    defaultColDef
                }
                columnDefs={gridData.cols}
                groupDisplayType="groupRows"
                autoGroupColumnDef={autoGroupColumnDef}
                //groupIncludeFooter={showGroupingFooter}
                groupTotalRow={showGroupingFooter ? "bottom" : undefined}
                components={{
                    shortcutsCellRenderer: ShortcutsCellRenderer,
                    memoCellRenderer: MemoCellRenderer,
                    memoCellRendererV2: MemoCellRendererV2,
                    systemBalanceCellRenderer: SystemBalanceCellRenderer,
                    sparklineCellRenderer: SparklineCellRenderer,
                    standardCellRenderer: StandardCellRenderer
                }}
                groupRowRendererParams={{
                    innerRenderer: SystemBalanceCellRenderer
                }}
                initialGroupOrderComparator={(params: InitialGroupOrderComparatorParams) => {
                    if (groupComparatorFunc.current) {
                        return groupComparatorFunc.current(params.nodeA, params.nodeB);
                    }
                    return 0;
                }

                }
                //suppressReactUi={true}
                rowBuffer={100}
                // defaultGroupOrderComparator={
                //     (nodeA: RowNode, nodeB: RowNode) => {
                //         if (groupComparatorFunc.current) {
                //             return groupComparatorFunc.current(nodeA, nodeB);
                //         }
                //     }
                // }
                groupDefaultExpanded={1}
                getRowClass={
                    (params: any) => {
                        if (params?.data?.hasCommError) {
                            return "comm-error";
                        }
                        return "";
                    }
                }
                // enableCharts={true}
                cellSelection={!isPhone && !isTablet}
                postSortRows={
                    (params: PostSortRowsParams) => {

                        const rowNodes = params.nodes;
                        if (postSortFunc.current) {
                            postSortFunc.current(rowNodes);
                        }
                    }
                }
                //animateRows={true}
                suppressAggFuncInHeader={true}
                //skipHeaderOnAutoSize={true} // => Handled in DefaultGrid
                quickFilterText={filterText}
                onFilterChanged={onFilterChanged}
                //maintainColumnOrder={true} // => Handled in DefaultGrid
                //suppressCellSelection={isPhone || isTablet}
                suppressCellFocus={isPhone || isTablet}
                suppressMovableColumns={isPhone || isTablet}
                suppressContextMenu={hideContextMenu}
                // SPR - See comment above about the column state.
                // onColumnPinned={onColumnPinned}

                domLayout={domLayout}
                //isMultiSection={isMultiSection}

                pagination={pagination}
                paginationPageSize={pageSize}
                suppressPaginationPanel={!pagination}

                paginateChildRows={pagination}

            // rowHeight={42}
            // headerHeight={49}

            // Balham
            // rowHeight={28}
            // headerHeight={33}
            //api={gridApi}
            //columnApi={columnApi}
                getRowId={getRowId}
            />

        </Box >
    );
}